import axios, { type AxiosResponse } from "axios";
import {
  ProfileItemSchema,
  ProfileImageSchema,
  AccountItemSchema,
} from "@/schemas/index";
import type {
  ProfileItemType,
  ProfileImageType,
  AccountItemType,
} from "@/schemas/index";
import env from "@/app/config/env";

export interface ServiceProfileApiType {
  fetchProfile: () => Promise<ProfileItemType>;
  fetchProfileAccounts: () => Promise<AccountItemType[]>;
  fetchUpdateCurrentAccount: (
    account: AccountItemType,
  ) => Promise<AccountItemType | null>;
  fetchUpdateProfile: (
    data: ProfileItemType,
  ) => Promise<ProfileItemType | null>;
  removeAccountProfile: () => Promise<ProfileItemType>;
  fetchUpdateProfileImage: (imageBase64: ProfileImageType) => Promise<boolean>;
  fetchRemoveProfileImage: () => Promise<boolean>;
  fetchProfileImage: () => Promise<ProfileImageType>;
  fetchUserImage: (
    login: ProfileItemType["Login"],
  ) => Promise<ProfileImageType | null>;
}

export const serviceProfileApi: ServiceProfileApiType = {
  fetchProfile: () => {
    return axios
      .get(`${env.services.portal}/me`)
      .then((response: AxiosResponse<unknown>) => {
        return ProfileItemSchema.parse(response.data);
      });
  },
  fetchProfileAccounts: () => {
    return axios
      .get(`${env.services.portal}/me/accounts`)
      .then((response: AxiosResponse<unknown[]>) => {
        return (
          response.data?.map((record: unknown) =>
            AccountItemSchema.parse(record),
          ) || []
        );
      });
  },
  fetchUpdateCurrentAccount: (account) => {
    return axios
      .put(`${env.services.portal}/me/accounts`, account)
      .then(() => {
        return AccountItemSchema.parse(account);
      })
      .catch(() => {
        return null;
      });
  },
  fetchUpdateProfile: (data: ProfileItemType) => {
    return axios.post(`${env.services.portal}/me`, data);
  },
  removeAccountProfile: () => {
    return axios.delete(`${env.services.portal}/me`);
  },
  fetchUpdateProfileImage: (imageBase64: ProfileImageType) => {
    return axios
      .post(`${env.services.portal}/me/image`, {
        Image: imageBase64,
      })
      .then(() => true)
      .catch(() => false);
  },
  fetchRemoveProfileImage: () => {
    return axios
      .delete(`${env.services.portal}/me/image`)
      .then(() => true)
      .catch(() => false);
  },
  fetchUserImage: (login: string) => {
    return axios
      .get(`${env.services.portal}/users/${login}/image`)
      .then((response: AxiosResponse<unknown>) => {
        return ProfileImageSchema.parse(response.data);
      })
      .catch(() => {
        return undefined;
      });
  },
  fetchProfileImage: () => {
    return axios
      .get(`${env.services.portal}/me/image`)
      .then((response: AxiosResponse<unknown>) => {
        return ProfileImageSchema.parse(response.data);
      })
      .catch(() => {
        return undefined;
      });
  },
};
